/**
 * Thin client for the NextProduct CRM shop API.
 * The CRM is the single source of truth: no local product/order/account storage.
 */
export const CRM_BASE =
  process.env.REACT_APP_CRM_BASE || "https://crm.nextproduct.nl/api";

/** Origin without /api — needed to render relative image paths (briefing §6.3). */
export const CRM_ORIGIN = CRM_BASE.replace(/\/api\/?$/, "");

export const TOKEN_STORAGE_KEY = "shop_token";
export const UNAUTHORIZED_EVENT = "shop:unauthorized";

export class ApiError extends Error {
  constructor(status, detail, messageKey) {
    super(detail || `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail || "";
    this.messageKey = messageKey || null;
  }
}

function detailFromBody(body) {
  if (!body) return "";
  if (typeof body.detail === "string") return body.detail;
  if (Array.isArray(body.detail)) {
    return body.detail
      .map((d) => (typeof d === "string" ? d : d?.msg))
      .filter(Boolean)
      .join(" ");
  }
  return "";
}

/**
 * Perform a call against the CRM API.
 * Errors are normalised to ApiError with a translation key for generic cases.
 */
export async function apiCall(method, path, { body, token } = {}) {
  let res;
  try {
    res = await fetch(`${CRM_BASE}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    throw new ApiError(0, "", "errors.network");
  }

  if (res.status === 401) {
    // Invalid/expired token → logout + redirect to /login (briefing §8)
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
    throw new ApiError(401, "", "errors.unauthorized");
  }

  if (!res.ok) {
    const data = await res.json().catch(() => null);
    const detail = detailFromBody(data);
    const key = res.status >= 500 ? "errors.server" : detail ? null : "errors.generic";
    throw new ApiError(res.status, detail, key);
  }

  if (res.status === 204) return null;
  return res.json();
}

export function readStoredToken() {
  try {
    return window.localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch (e) {
    return null;
  }
}

export function storeToken(token) {
  try {
    if (token) window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    else window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch (e) {
    /* ignore */
  }
}
